/**
 * The once-ever gate for the "You're all set up" celebration.
 *
 * `GET /academy/setup` only reports the current all-true state — it has no
 * "just became complete" edge. So the modal arms itself the first time a
 * browser sees the checklist mid-setup, and only fires when an armed browser
 * watches the last item complete. "Complete" is the checklist's own
 * definition (`allComplete`): an API flag for required items, and a flag or
 * a visited/skipped mark for optional ones — the same count the badge shows
 * as 9/9. An academy that was already finished before this shipped retires
 * straight to `seen` and never gets a surprise popup.
 *
 * Pure so the four-way transition stays testable; the localStorage read and
 * write live in the component.
 */
export type SetupCompleteState = "armed" | "seen";

export interface SetupCompleteStep {
  /** The value to persist, or null to leave storage untouched. */
  store: SetupCompleteState | null;
  celebrate: boolean;
}

export const nextSetupCompleteStep = (
  stored: SetupCompleteState | null | undefined,
  complete: boolean,
): SetupCompleteStep => {
  // Terminal: the coach has had their moment (or never qualified for one).
  if (stored === "seen") {
    return { store: null, celebrate: false };
  }

  if (!complete) {
    // Idempotent — don't rewrite a flag that already says "armed".
    return { store: stored === "armed" ? null : "armed", celebrate: false };
  }

  return { store: "seen", celebrate: stored === "armed" };
};
